import { useState } from 'react';
import { useMarketingMaterials } from '../hooks/useMarketingMaterials';
import MaterialCard from './MaterialCard';
import MaterialPreviewModal from './MaterialPreviewModal';
import type { PublicMaterial } from '@/types';

interface MaterialsSelectionPanelProps {
  selectedMaterialIds: string[];
  onToggleMaterial: (materialId: string) => void;
}

/**
 * Displays a scrollable list of public marketing materials with selection controls
 * Handles loading, error, and empty states
 */
export default function MaterialsSelectionPanel({ 
  selectedMaterialIds, 
  onToggleMaterial 
}: MaterialsSelectionPanelProps) {
  const { data, isLoading, isError, refetch } = useMarketingMaterials();
  const [previewMaterial, setPreviewMaterial] = useState<PublicMaterial | null>(null);

  const handlePreview = (material: PublicMaterial) => {
    setPreviewMaterial(material);
  };

  const closePreview = () => {
    setPreviewMaterial(null);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="border rounded-lg p-8 bg-gray-50">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
          <span className="ml-3 text-sm text-gray-600">Loading materials...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <div className="border border-red-200 rounded-lg p-6 bg-red-50">
        <div className="flex items-start gap-3">
          <span className="text-2xl">⚠️</span>
          <div className="flex-1">
            <h4 className="font-semibold text-sm text-red-900">Unable to load materials</h4>
            <p className="text-sm text-red-700 mt-1">
              The marketing materials service is temporarily unavailable. Please try again.
            </p>
            <button
              onClick={() => refetch()}
              className="mt-3 px-4 py-2 bg-red-600 text-white rounded text-sm hover:bg-red-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (!data || data.materials.length === 0) {
    return (
      <div className="border border-gray-200 rounded-lg p-8 bg-gray-50 text-center">
        <span className="text-4xl">📭</span>
        <h4 className="font-semibold text-sm text-gray-700 mt-3">No materials available</h4>
        <p className="text-sm text-gray-500 mt-1">
          There are no public marketing materials to attach at this time.
        </p>
      </div>
    );
  }

  const handleSelect = (materialId: string) => {
    if (selectedMaterialIds.length >= 10) {
      // Max 10 materials per reply
      alert('You can attach a maximum of 10 materials per reply.');
      return;
    }
    onToggleMaterial(materialId);
  };

  const handleDeselect = (materialId: string) => {
    onToggleMaterial(materialId);
  };

  return (
    <div className="border rounded-lg bg-white" role="region" aria-label="Marketing materials selection">
      <div className="p-4 border-b bg-gray-50">
        <h3 className="font-semibold text-sm">Select Marketing Materials</h3>
        <p className="text-xs text-gray-600 mt-1">
          Choose up to 10 materials to attach (max 20MB per file)
        </p>
        {selectedMaterialIds.length > 0 && (
          <p className="text-xs text-brand font-medium mt-2" aria-live="polite">
            {selectedMaterialIds.length} of 10 materials selected
          </p>
        )}
      </div>
      
      <div className="p-4 max-h-96 overflow-y-auto space-y-3" role="list" aria-label="Available marketing materials">
        {data.materials.map((material) => (
          <MaterialCard
            key={material.documentId}
            material={material}
            isSelected={selectedMaterialIds.includes(material.documentId)}
            onSelect={handleSelect}
            onDeselect={handleDeselect}
            onPreview={handlePreview}
          />
        ))}
      </div>

      {/* Preview Modal */}
      <MaterialPreviewModal
        material={previewMaterial}
        isOpen={!!previewMaterial}
        onClose={closePreview}
      />
    </div>
  );
}
